// Helpers for reading a destination's record_schema, a draft-07 JSON Schema.

export type DestinationField = {
  name: string;
  type: string;
  required: boolean;
  schema: unknown;
};

type JsonObject = Record<string, unknown>;

export const isJsonObject = (value: unknown): value is JsonObject =>
  typeof value === "object" && value !== null && !Array.isArray(value);

export const parseMaybeJson = (value: unknown): unknown => {
  if (typeof value !== "string") {
    return value;
  }

  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

export const formatSchemaType = (schema: unknown): string => {
  if (!isJsonObject(schema)) {
    return "unknown";
  }

  const type = schema.type;
  if (Array.isArray(type)) {
    return type.filter((item): item is string => typeof item === "string").join(" | ") || "unknown";
  }

  if (typeof type === "string") {
    return type;
  }

  return "unknown";
};

export const getDestinationFields = (recordSchema: unknown): DestinationField[] => {
  const schema = parseMaybeJson(recordSchema);
  if (!isJsonObject(schema) || !isJsonObject(schema.properties)) {
    return [];
  }

  const requiredFields = Array.isArray(schema.required)
    ? new Set(schema.required.filter((field): field is string => typeof field === "string"))
    : new Set<string>();

  return Object.entries(schema.properties).map(([name, property]) => ({
    name,
    type: formatSchemaType(property),
    required: requiredFields.has(name),
    schema: property,
  }));
};
